/**
 * MisApprovalAgGridHook
 *
 * AG Grid hook for the Settlement MIS Approval LiveView.
 *
 * Columns: Date, Status, Txns, Merchants, Gross (AED), MDR, VAT, Net Payable
 *
 * LiveView events received (via handleEvent):
 *   "update_mis_data" – { rows: [...] }
 *
 * LiveView events pushed (via pushEvent):
 *   "mis_row_clicked" – { id: <mis_id> }
 */

const MisApprovalAgGridHook = {
  mounted() {
    const container = this.el;
    const rows = JSON.parse(container.dataset.rows || "[]");

    // ── Cell renderers ─────────────────────────────────────────────────────

    const statusBadge = (params) => {
      const val = params.value || "";
      const map = {
        pending:         { bg: "#F3F4F6", color: "#374151", label: "pending" },
        level1_approved: { bg: "#FEF3C7", color: "#92400E", label: "L1 Approved" },
        approved:        { bg: "#D1FAE5", color: "#065F46", label: "approved" },
        discarded:       { bg: "#FEE2E2", color: "#991B1B", label: "discarded" },
      };
      const s = map[val] || map.pending;
      return `<span style="background:${s.bg};color:${s.color};padding:2px 10px;
        border-radius:9999px;font-size:11px;font-weight:600;">${s.label}</span>`;
    };

    const amountCell = (params) => {
      const val = params.value;
      if (val === null || val === undefined || val === "") return "0.00";
      const n = parseFloat(val);
      return isNaN(n)
        ? val
        : n.toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // ── Actions cell renderer ──────────────────────────────────────────────

    const self = this;

    const actionsRenderer = (params) => {
      if (!params.data) return "";
      const { id, approval_status } = params.data;
      const wrap = document.createElement("div");
      wrap.style.cssText = "display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:2px 0;";

      const btn = (label, color, onClick) => {
        const el = document.createElement("button");
        el.textContent = label;
        el.style.cssText = `color:${color};font-size:11px;cursor:pointer;background:none;border:none;padding:0;white-space:nowrap;`;
        el.addEventListener("click", (e) => { e.stopPropagation(); onClick(); });
        return el;
      };

      // Details — opens the inline detail panel
      wrap.appendChild(btn("Details", "#4f46e5", () =>
        self.pushEvent("mis_row_clicked", { id })
      ));

      if (approval_status === "pending") {
        wrap.appendChild(btn("L1 Approve", "#16a34a", () =>
          self.pushEvent("open_approve", { id: String(id), level: "l1" })
        ));
      }

      if (approval_status === "level1_approved") {
        const b = btn("L2 Approve", "#15803d", () =>
          self.pushEvent("open_approve", { id: String(id), level: "l2" })
        );
        b.style.fontWeight = "600";
        wrap.appendChild(b);
      }

      if (approval_status !== "approved" && approval_status !== "discarded") {
        wrap.appendChild(btn("Reject", "#dc2626", () =>
          self.pushEvent("open_reject", { id: String(id) })
        ));
      }

      // CSV download link
      const link = document.createElement("a");
      link.textContent = "CSV";
      link.href = `/api/v1/settlements/mis/${id}/download`;
      link.target = "_blank";
      link.style.cssText = "color:#6b7280;font-size:11px;text-decoration:none;white-space:nowrap;";
      link.addEventListener("click", (e) => e.stopPropagation());
      wrap.appendChild(link);

      return wrap;
    };

    // ── Column definitions ─────────────────────────────────────────────────

    const columnDefs = [
      { field: "id", hide: true },
      {
        field: "mis_date",
        headerName: "Date",
        pinned: "left",
        width: 130,
        sort: "desc",
        cellClass: "font-mono text-xs",
        filter: "agTextColumnFilter",
      },
      {
        field: "approval_status",
        headerName: "Status",
        flex: 1.3,
        minWidth: 140,
        cellRenderer: statusBadge,
        filter: "agTextColumnFilter",
      },
      {
        field: "total_transactions",
        headerName: "Txns",
        type: "numericColumn",
        flex: 0.6,
        minWidth: 70,
        filter: "agNumberColumnFilter",
      },
      {
        field: "total_merchants",
        headerName: "Merchants",
        type: "numericColumn",
        flex: 0.8,
        minWidth: 90,
        filter: "agNumberColumnFilter",
      },
      {
        field: "gross_total_amount",
        headerName: "Gross (AED)",
        type: "numericColumn",
        cellRenderer: amountCell,
        flex: 1,
        minWidth: 120,
        filter: "agNumberColumnFilter",
      },
      {
        field: "total_mdr_charges",
        headerName: "MDR",
        type: "numericColumn",
        cellRenderer: amountCell,
        flex: 0.8,
        minWidth: 100,
        filter: "agNumberColumnFilter",
      },
      {
        field: "total_vat",
        headerName: "VAT",
        type: "numericColumn",
        cellRenderer: amountCell,
        flex: 0.8,
        minWidth: 90,
        filter: "agNumberColumnFilter",
      },
      {
        field: "total_net_payable",
        headerName: "Net Payable",
        type: "numericColumn",
        cellRenderer: amountCell,
        flex: 1.1,
        minWidth: 140,
        cellStyle: { fontWeight: "bold" },
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Actions",
        field: "id",
        colId: "actions",
        pinned: "right",
        width: 260,
        sortable: false,
        filter: false,
        floatingFilter: false,
        resizable: true,
        cellRenderer: actionsRenderer,
      },
    ];

    // ── Grid initialization ────────────────────────────────────────────────

    this.grid = window.agGrid.createGrid(container, {
      columnDefs,
      rowData: rows,
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true,
        flex: 1,
        minWidth: 90,
      },
      rowSelection: "single",
      rowClass: "cursor-pointer",
      sideBar: {
        toolPanels: [
          {
            id: "columns",
            labelDefault: "Columns",
            labelKey: "columns",
            iconKey: "columns",
            toolPanel: "agColumnsToolPanel",
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true,
              suppressColumnFilter: false,
              suppressColumnSelectAll: false,
              suppressColumnExpandAll: false,
            },
          },
        ],
      },
      onRowClicked: (e) => {
        if (e.data) this.pushEvent("mis_row_clicked", { id: e.data.id });
      },
      pagination: true,
      paginationPageSize: 10,
      paginationPageSizeSelector: [10, 20, 50, 100],
      popupParent: document.body,
      suppressMovableColumns: false,
    });

    // ── Server-push handler ────────────────────────────────────────────────

    this.handleEvent("update_mis_data", ({ rows }) => {
      this.grid.setGridOption("rowData", rows);
    });
  },

  updated() {
    // phx-update="ignore" keeps LiveView from patching the grid element.
    // This callback fires only for non-ignored ancestor re-renders.
    // Just trigger a resize so AG Grid recalculates layout if needed.
    if (this.grid) setTimeout(() => window.dispatchEvent(new Event("resize")), 0);
  },

  destroyed() {
    if (this.grid) {
      this.grid.destroy();
      this.grid = null;
    }
  },
};

export default MisApprovalAgGridHook;
